import { useMemo } from "react";
import {
  ContributorConnection,
  SYSTEM_CONTRIBUTION_TYPES_LABELS,
  SystemContributorTypes,
} from "../../../types/contributor";
import {
  SystemEntityCollections,
  SystemEntityProofreadingStatus,
  SystemLanguages,
} from "../../../shared/enums/shared-enums";
import { initMetadata } from "../../../controllers/formController";
import useEvidenceLogic from "../evidenceLogic";
import { Person } from "../../../types/person";
import { Version } from "../../../types/version";

const useContributorLogic = (
  from: Version | null,
  to: Person | null,
  contributorType: SystemContributorTypes,
  language?: string
) => {
  const {
    internalEvidenceTypeEnum,
    defaultNameExternalEvidence,
    defaultNameInternalEvidence,
    externalEvidenceTypeEnum,
  } = useEvidenceLogic();

  const systemLanguageType = useMemo<
    ContributorConnection["system_language_type"] | null
  >(() => {
    if (!Object.values(SystemLanguages).includes(language as SystemLanguages)) {
      return null;
    }
    return language as ContributorConnection["system_language_type"];
  }, [language]);

  const metadataFieldNames = useMemo<Array<keyof ContributorConnection>>(
    () => ["status", "name_internal_evidence", "name_external_evidence"],
    []
  );

  const contributorStatusEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return `contributor_status`;
  }, [systemLanguageType]);

  const contributorPlaceIdentificationStatusEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return `contributor_place_identification_status`;
  }, [systemLanguageType]);

  const roleEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (contributorType) {
      case SystemContributorTypes.PETITIONER:
      case SystemContributorTypes.SCRIBE:
      case SystemContributorTypes.PATRON:
        return `contributor_${contributorType}_role`;
      default:
        return "";
    }
  }, [systemLanguageType, contributorType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      internalEvidenceTypeEnum,
      externalEvidenceTypeEnum,
      contributorStatusEnum,
      contributorPlaceIdentificationStatusEnum,
      roleEnum,
    ].filter(Boolean);
  }, [
    systemLanguageType,
    roleEnum,
    internalEvidenceTypeEnum,
    externalEvidenceTypeEnum,
    contributorStatusEnum,
    contributorPlaceIdentificationStatusEnum,
  ]);

  const defaultContributor = useMemo<ContributorConnection>(() => {
    if (systemLanguageType === null) {
      throw new Error("Invalid system language type");
    }
    return {
      _from: from?._id || "",
      _to: to?._id || "",
      _id: "",
      _key: "",
      _rev: "",
      system_contribution_type: contributorType,
      system_language_type: systemLanguageType,
      status: "",
      role: "",
      place_id: "",
      name: "",
      place: null,
      place_identification_status: "",
      name_internal_evidence: defaultNameInternalEvidence,
      name_external_evidence: defaultNameExternalEvidence,
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      internal_notes: "",
      notes: "",
      fromDoc: from
        ? { ...from, _collection: SystemEntityCollections.VERSION }
        : null,
      toDoc: to ? { ...to, _collection: SystemEntityCollections.PERSON } : null,
      attributes_metadata:
        initMetadata<ContributorConnection>(metadataFieldNames),
    };
  }, [
    from,
    to,
    metadataFieldNames,
    systemLanguageType,
    contributorType,
    defaultNameInternalEvidence,
    defaultNameExternalEvidence,
  ]);

  const typeBasedFields = useMemo<Array<keyof ContributorConnection>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (contributorType) {
      case SystemContributorTypes.REVEALER:
      case SystemContributorTypes.COMMISSIONER:
      case SystemContributorTypes.ANNOTATOR:
      case SystemContributorTypes.AUTHOR:
        return ["status"];
      case SystemContributorTypes.PETITIONER:
      case SystemContributorTypes.SCRIBE:
      case SystemContributorTypes.PATRON:
        return ["status", "role"];
      default:
        return [];
    }
  }, [systemLanguageType, contributorType]);

  const showField = (field: keyof ContributorConnection): boolean => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return typeBasedFields.includes(field);
  };

  const getDisplayName = (contributor: ContributorConnection) =>
    `${SYSTEM_CONTRIBUTION_TYPES_LABELS[contributor.system_contribution_type]} Contributor Connection`;

  return {
    userEnums,
    defaultContributor,
    metadataFieldNames,
    getDisplayName,
    contributorStatusEnum,
    contributorPlaceIdentificationStatusEnum,
    roleEnum,
    showField,
  };
};

export default useContributorLogic;
